import { Component, OnInit } from '@angular/core';
import { SharedModule } from '../../shared/shared.module';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthServiceService } from '../../core/services/authService/auth-service.service';
import { ToastrService } from 'ngx-toastr';
import { Roles } from '../../constants/roles.enum';
import { filter, take } from 'rxjs';
import { AuthRedirectService } from '@edtech-ui/core/services/auth-redirect/auth-redirect.service';

@Component({
	selector: 'app-user-registration-auth-callback',
	standalone: true,
	imports: [SharedModule],
	templateUrl: './user-registration-auth-callback.component.html',
	styleUrl: './user-registration-auth-callback.component.scss'
})
export class UserRegistrationAuthCallbackComponent implements OnInit {

	isLoading: boolean = false;
	isInviteTeacherGoogle: boolean = false;
	isTenantOnBoardGoogle: boolean = false;
	redirectUrl: string | null = null;

	constructor(
		private route: ActivatedRoute,
		private _authService: AuthServiceService,
		private router: Router,
		private _toastr: ToastrService,
		private _authRedirectService: AuthRedirectService,
	) { }

	ngOnInit() {
		this.route.queryParams
			.pipe(
				take(1),
				filter(params => !!params['code'])
			)
			.subscribe({
				next: (params) => {
					const redirectState = params['state'] ? decodeURIComponent(params['state']) : null;
					this.redirectUrl = redirectState;
					this.googleAuth(params['code'])
				},
				error: () => {
					this._toastr.error('Failed to read query parameters');
					this.router.navigate(['']);
				}
			});
		this._authRedirectService.redirectIfLoggedIn();
	}



	googleAuth(code: string) {
		this.isLoading = true;

		this._authService.postGoogleSignUp({ code, is_default: true , registration_source:'web' }).subscribe({
			next: (response: any) => {
				this.isLoading = false;
				const email = response.Data.email || response.Data.phone_email;
				const name = response.Data.name;
				const roleId = response.Data.role_id;

				if (response.Success && !response.Data.token && !response.Data.refresh_token) {
					this.router.navigate(['/mobile-verify'], { queryParams: { email, name, google: true, redirectUrl: this.redirectUrl } });
					return;
				}

				if (response?.Success && response.Data) {
					this._toastr.success('Authentication successful');

					const token = response.Data.token;
					const refresh_token = response.Data.refresh_token;
					const userId = response.Data.user_id;

					localStorage.setItem('token', token);
					localStorage.setItem('refresh_token', refresh_token);
					localStorage.setItem('email', email);
					localStorage.setItem('user_id', userId);
					localStorage.setItem('is_tenant_onboard', JSON.stringify(response.Data.is_tenant_onboard));

					this.isInviteTeacherGoogle = response.Data.is_teacher;
					this.isTenantOnBoardGoogle = response.Data.is_tenant_onboard;

					const roleModeGoogle = this.getRoleModeGoogle();
					localStorage.setItem('role_mode_google', roleModeGoogle);

					this._authService.currentUserEmail.next(email);
					this._authService.currentUserId.next(userId);
					this._authService.isTenantOnBoardForGoogleAuth.next(response.Data.is_tenant_onboard);

					if (this.redirectUrl) {
						this._authRedirectService.handlePostLoginRedirect(this.redirectUrl);
						return;
					}

					if (roleId) {
						localStorage.setItem('role_id', roleId.toString());

						switch (roleId) {
							case Roles.TEACHER:
								this.router.navigate(['/teacher-dashboard']);
								break;
							case Roles.STUDENT:
								this.router.navigate(['/student-dashboard']);
								break;
							case Roles.ADMIN:
								this.router.navigate(['/admin-dashboard']);
								break;

							case Roles.StudentPlatformAdmin:
								this.router.navigate(['/admin-student-platform']);
								break;

							default:
								this._toastr.error('Unknown role. Please contact support.');
								this.router.navigate(['/login']);
						}
					} else {
						localStorage.removeItem('role_id');
						this.router.navigate(['/signup']);
					}

				} else {
					this._toastr.error(response?.Message || 'Google authentication failed.');
					this.router.navigate(['/login']);
				}
			},
			error: () => {
				this.isLoading = false;
				this._toastr.error('Error exchanging code for token');
				this.router.navigate(['/login']);
			}
		});
	}

	getRoleModeGoogle(): 'teacher' | 'student' | 'both' {
		if (this.isInviteTeacherGoogle && this.isTenantOnBoardGoogle) {
			return 'teacher';
		}

		if (!this.isInviteTeacherGoogle && this.isTenantOnBoardGoogle) {
			return 'student';
		}

		return 'both';
	}


	private decodeRedirect(url: string): string | null {
		try {
			const decoded = decodeURIComponent(decodeURIComponent(url));
			return decoded;
		} catch (error) {
			this._toastr.error('URL decoding error:', 'error');
			return null;
		}
	}
}
